/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../api';
import type { Invoice, InvoiceDocumentType, InvoiceLine } from '../../api/areas/commerce';
import { Callout, DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { BulkList, useSelection } from '../../components/BulkList';
import { CountTiles } from '../../components/CountTiles';
import { SavedViews } from '../../components/SavedViews';
import { DataError } from '../../components/DataError';
import { DomainLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { InvoiceQrAction } from './PaymentQr';
import { EmptyState, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { fractionToPercent } from './PricingPage';
import { EInvoiceSection } from './EInvoiceSection';
import { CustomerListFilter } from './CustomerListFilter';
import {
  Amount,
  clearParams,
  CustomerRef,
  docNumber,
  DOC_TYPE,
  FilterSelect,
  FilterText,
  InvoiceRef,
  mutedSmall,
  OrderRef,
  Pager,
  RefreshButton,
  ResetFilters,
  RichText,
  setParam,
  Status,
  useEnumLabel,
  useKeyset,
  When,
} from './shared';

const FILTER_KEYS = ['document_type', 'series', 'customer_id'] as const;

function EInvoiceNotice({ className }: { className?: string }) {
  const { t } = useTranslation('commerce');
  return (
    <Callout tone="info" title={t('invoices.limitTitle')} className={className}>
      {t('invoices.limit')}
    </Callout>
  );
}

/** `/invoices`: issued invoices and credit notes, gap-free per series and immutable; tiles per document type, saved views, selection. */
export function InvoicesPage() {
  const { t } = useTranslation('commerce');
  const { t: tu } = useTranslation('ux');
  const label = useEnumLabel('commerce');
  useDocumentTitle(t('invoices.title'));
  const [params, setParams] = useSearchParams();
  const type = (params.get('document_type') ?? '') as InvoiceDocumentType | '';
  const series = params.get('series') ?? '';
  const customer = params.get('customer_id') ?? '';
  const pager = useKeyset(`${type}|${series}|${customer}`);
  const result = useApi(
    (signal) => api.listInvoices({ document_type: type || undefined, series: series || undefined, customer_id: customer || undefined, limit: 50, before: pager.before }, { signal }),
    [type, series, customer, pager.before],
  );
  const counts = useApi((signal) => api.getCounts({ signal }), []);
  // Issued documents cannot be changed: the selection is exported.
  const selection = useSelection<Invoice>((i) => i.id);
  const rows = result.status === 'success' ? result.data.items : [];
  const columns: Column<Invoice>[] = [
    selection.column(rows, (i) => tu('bulkList.select', { name: docNumber(i) }), tu('bulkList.selectAll')),
    { key: 'number', header: t('invoices.col.number'), cell: (i) => <InvoiceRef invoice={i} /> },
    { key: 'document', header: t('invoices.col.document'), cell: (i) => <Status group="docType" tones={DOC_TYPE} value={i.document_type} /> },
    { key: 'customer', header: t('invoices.col.customer'), cell: (i) => <CustomerRef id={i.customer_id} name={i.customer_name} /> },
    { key: 'order', header: t('invoices.col.order'), cell: (i) => <OrderRef id={i.order_id} domain={i.order_domain} count={i.order_item_count} at={i.order_placed_at} /> },
    { key: 'subtotal', header: t('invoices.col.subtotal'), numeric: true, cell: (i) => <Amount amount={i.subtotal} currency={i.currency} /> },
    { key: 'tax', header: t('invoices.col.tax'), numeric: true, cell: (i) => <Amount amount={i.tax} currency={i.currency} /> },
    { key: 'total', header: t('invoices.col.total'), numeric: true, cell: (i) => <Amount amount={i.total} currency={i.currency} className="font-semibold" /> },
    { key: 'issued', header: t('invoices.col.issued'), cell: (i) => <When at={i.issued_at} /> },
  ];
  return (
    <>
      <PageHeader title={t('invoices.title')} crumbs={[{ label: t('crumb') }]} subtitle={<RichText k="invoices.subtitle" />} actions={<RefreshButton onClick={result.reload} />} />
      <EInvoiceNotice className="mb-6" />
      <CountTiles
        label={t('invoices.documentFilter')}
        allLabel={tu('lists.all')}
        options={Object.keys(DOC_TYPE).map((d) => ({ value: d, label: label('docType', d), tone: DOC_TYPE[d] }))}
        counts={counts.status === 'loading' ? null : counts.status === 'success' ? (counts.data.invoices?.by_document_type ?? undefined) : undefined}
        current={type}
        onPick={(v) => setParams(setParam(params, 'document_type', v))}
      />
      <FilterBar actions={<ResetFilters show={Boolean(type || series || customer)} onReset={() => setParams(clearParams(params, FILTER_KEYS))} />}>
        <CustomerListFilter value={customer} name={rows[0]?.customer_name} onChange={(v) => setParams(setParam(params, 'customer_id', v))} />
        <FilterSelect label={t('invoices.documentFilter')} value={type} options={Object.keys(DOC_TYPE)} group="docType" onChange={(v) => setParams(setParam(params, 'document_type', v))} />
        <FilterText label={t('invoices.seriesFilter')} value={series} className="sm:w-40" onChange={(v) => setParams(setParam(params, 'series', v))} placeholder={t('invoices.seriesPlaceholder')} />
      </FilterBar>
      <SavedViews list="invoices" keys={FILTER_KEYS} />
      <BulkList
        selection={selection}
        actions={[]}
        rowKey={(i) => i.id}
        rowLabel={docNumber}
        onDone={result.reload}
        csv={{
          name: 'invoices',
          columns: [
            { header: t('invoices.col.number'), value: docNumber },
            { header: t('invoices.col.document'), value: (i) => label('docType', i.document_type) },
            { header: t('invoices.col.customer'), value: (i) => i.customer_name },
            { header: tu('lists.csv.domains'), value: (i) => i.order_domain },
            { header: t('invoices.col.subtotal'), value: (i) => i.subtotal },
            { header: t('invoices.col.tax'), value: (i) => i.tax },
            { header: t('invoices.col.total'), value: (i) => i.total },
            { header: tu('lists.csv.currency'), value: (i) => i.currency },
            { header: t('invoices.col.issued'), value: (i) => i.issued_at },
          ],
        }}
      />
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('invoices.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={rows} rowKey={(i) => i.id} empty={<EmptyState title={t('invoices.empty')} />} />
            <Pager cursors={pager.cursors} next={result.data.next_before} onNewer={pager.newer} onOlder={pager.older} />
          </>
        ) : null}
      </Section>
    </>
  );
}

/** `/invoices/:invoiceId`: one issued document, its e-invoices, its lines and the documents it relates to. */
export function InvoiceDetailPage() {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const { invoiceId = '' } = useParams();
  useDocumentTitle(t('invoice.title'));
  const result = useApi((signal) => api.getInvoice(invoiceId, { signal }), [invoiceId]);
  const crumbs = [{ label: t('crumb') }, { label: t('invoices.title'), to: '/invoices' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('invoice.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('invoice.loadError')} />
      </>
    );
  }
  const { invoice: i, lines, related } = result.data;
  const credited = related.find((r) => r.id === i.credits_invoice_id);
  const lineColumns: Column<InvoiceLine>[] = [
    {
      key: 'description',
      header: t('invoice.lineCol.description'),
      className: 'whitespace-normal',
      cell: (l) => (
        <span className="block min-w-48">
          {l.domain_name ? <DomainLink keyOrName={l.domain_name} /> : null}
          <span className={l.domain_name ? `${mutedSmall} block` : undefined}>
            {l.description}
            {l.is_credit ? <span className={mutedSmall}> {t('invoice.credit')}</span> : null}
          </span>
        </span>
      ),
    },
    { key: 'qty', header: t('invoice.lineCol.qty'), numeric: true, cell: (l) => l.quantity },
    { key: 'unit', header: t('invoice.lineCol.unitPrice'), numeric: true, cell: (l) => <Amount amount={l.unit_price} currency={i.currency} /> },
    { key: 'amount', header: t('invoice.lineCol.amount'), numeric: true, cell: (l) => <Amount amount={l.amount} currency={i.currency} /> },
    {
      key: 'taxCode',
      header: t('invoice.lineCol.tax'),
      className: 'whitespace-nowrap',
      // The rate is stored as a fraction; an invoice line has to read as a percentage.
      cell: (l) => (l.tax_code ? `${l.tax_code}${l.tax_rate ? ` ${fractionToPercent(l.tax_rate)} %` : ''}` : '—'),
    },
    { key: 'taxAmount', header: t('invoice.lineCol.taxAmount'), numeric: true, cell: (l) => <Amount amount={l.tax_amount} currency={i.currency} /> },
  ];
  return (
    <>
      <PageHeader
        title={`${label('docType', i.document_type)} ${docNumber(i)}`}
        crumbs={[...crumbs, { label: docNumber(i) }]}
        actions={
          <>
            <InvoiceQrAction invoice={i} />
            <RefreshButton onClick={result.reload} />
          </>
        }
      />
      <div className="flex flex-col gap-6">
        <Section>
          <div className="space-y-4">
            <KeyValue
              columns={3}
              rows={[
                [t('invoice.field.document'), <Status key="d" group="docType" tones={DOC_TYPE} value={i.document_type} />],
                [t('invoice.field.number'), <code key="n" className="font-mono">{docNumber(i)}</code>],
                [t('common.issued'), formatDateTime(i.issued_at)],
                [t('common.customer'), <CustomerRef key="c" id={i.customer_id} name={i.customer_name} />],
                [t('common.order'), <OrderRef key="o" id={i.order_id} domain={i.order_domain} count={i.order_item_count} at={i.order_placed_at} />],
                [t('invoice.field.credits'), credited ? <InvoiceRef key="cr" invoice={credited} /> : null],
                [t('common.subtotal'), <Amount key="s" amount={i.subtotal} currency={i.currency} />],
                [t('common.tax'), <Amount key="t" amount={i.tax} currency={i.currency} />],
                [t('common.total'), <Amount key="tt" amount={i.total} currency={i.currency} className="text-base font-semibold" />],
              ]}
            />
            <p className="text-xs text-muted-foreground">{t('invoice.immutable')}</p>
          </div>
        </Section>
        <EInvoiceSection invoiceId={i.id} />
        <Section title={t('invoice.buyer.title')} description={t('invoice.buyer.description')}>
          {i.buyer ? (
            <KeyValue
              columns={3}
              rows={[
                [t('invoice.buyer.name'), i.buyer.name],
                [t('invoice.buyer.taxCode'), i.buyer.tax_code ? <span key="tc" className="font-mono">{i.buyer.tax_code}</span> : '—'],
                [t('invoice.buyer.identityNumber'), i.buyer.identity_number_hint ? <span key="id" className="font-mono">{i.buyer.identity_number_hint}</span> : '—'],
                [t('invoice.buyer.address'), i.buyer.address ?? '—'],
                [t('invoice.buyer.emails'), i.buyer.emails.length ? i.buyer.emails.join(', ') : '—'],
              ]}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{t('invoice.buyer.none')}</p>
          )}
        </Section>
        <Section title={t('invoice.lines')} flush>
          <DataTable columns={lineColumns} rows={lines} rowKey={(l) => l.id} />
        </Section>
        {related.length ? (
          <Section title={i.document_type === 'CREDIT_NOTE' ? t('invoice.creditedInvoice') : t('invoice.creditNotes')}>
            <ul className="space-y-1.5 text-sm">
              {related.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2">
                  <Status group="docType" tones={DOC_TYPE} value={r.document_type} />
                  <InvoiceRef invoice={r} />
                  <Amount amount={r.total} currency={r.currency} />
                  <span className={mutedSmall}>{formatDateTime(r.issued_at)}</span>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}
      </div>
    </>
  );
}
